import { SentEmail, EmailStatus, TemplateType } from '../../../mocks/emails';

interface SentEmailsTableProps {
  emails: SentEmail[];
  onResend: (email: SentEmail) => void;
}

const statusConfig: Record<EmailStatus, { label: string; color: string; bg: string; icon: string }> = {
  Sent: { label: 'Sent', color: '#6366f1', bg: '#eef2ff', icon: 'ri-send-plane-line' },
  Delivered: { label: 'Delivered', color: '#0ea5e9', bg: '#f0f9ff', icon: 'ri-mail-check-line' },
  Opened: { label: 'Opened', color: '#10b981', bg: '#ecfdf5', icon: 'ri-mail-open-line' },
  Clicked: { label: 'Clicked', color: '#f59e0b', bg: '#fffbeb', icon: 'ri-cursor-line' },
  Bounced: { label: 'Bounced', color: '#f97316', bg: '#fff7ed', icon: 'ri-error-warning-line' },
  Failed: { label: 'Failed', color: '#ef4444', bg: '#fef2f2', icon: 'ri-close-circle-line' },
};

const typeColorMap: Partial<Record<TemplateType, string>> = {
  booking_confirmation: '#10b981',
  invoice: '#f59e0b',
  itinerary: '#8b5cf6',
  payment_receipt: '#06b6d4',
  voucher: '#ec4899',
  follow_up: '#f97316',
  custom: '#64748b',
};

function formatDateTime(dt: string) {
  const d = new Date(dt);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export default function SentEmailsTable({ emails, onResend }: SentEmailsTableProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Recipient</th>
            <th className="text-left px-4 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Subject</th>
            <th className="text-left px-4 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Template</th>
            <th className="text-left px-4 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Sent</th>
            <th className="text-left px-4 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Status</th>
            <th className="text-left px-4 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Attachments</th>
            <th className="px-4 py-3.5" />
          </tr>
        </thead>
        <tbody>
          {emails.map((email) => {
            const status = statusConfig[email.status];
            const typeColor = typeColorMap[email.templateType] || '#64748b';
            return (
              <tr key={email.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                      style={{ backgroundColor: typeColor }}
                    >
                      {email.toName.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{email.toName}</p>
                      <p className="text-xs text-gray-400">{email.to}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3.5">
                  <p className="text-sm text-gray-700 max-w-[220px] truncate">{email.subject}</p>
                  {email.bookingRef && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      <i className="ri-bookmark-3-line mr-0.5" />
                      {email.bookingRef}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3.5">
                  <span
                    className="text-xs px-2 py-1 rounded-full font-medium"
                    style={{ backgroundColor: typeColor + '15', color: typeColor }}
                  >
                    {email.templateName}
                  </span>
                </td>
                <td className="px-4 py-3.5">
                  <p className="text-xs text-gray-600 whitespace-nowrap">{formatDateTime(email.sentAt)}</p>
                  <p className="text-xs text-gray-400">{email.sentBy}</p>
                </td>
                <td className="px-4 py-3.5">
                  <div
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full font-medium whitespace-nowrap"
                    style={{ backgroundColor: status.bg, color: status.color }}
                  >
                    <i className={`${status.icon} text-sm`} />
                    {status.label}
                  </div>
                  {email.openedAt && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Opened {formatDateTime(email.openedAt).split(' ').slice(-1)}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3.5">
                  {email.attachments.length > 0 ? (
                    <div className="flex flex-col gap-0.5">
                      {email.attachments.map((att) => (
                        <span key={att} className="text-xs text-gray-500 flex items-center gap-1">
                          <i className="ri-file-pdf-line text-red-400" />
                          <span className="truncate max-w-[100px]">{att.replace(/_/g, ' ')}</span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-300">No files</span>
                  )}
                </td>
                <td className="px-4 py-3.5">
                  <button
                    onClick={() => onResend(email)}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line" />
                    Resend
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
